import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tooltip,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getWarehouses } from '../../services/warehouseService';
import { getLocations } from '../../services/locationService';
import {
  createPhysicalInventory,
  getPhysicalInventoryById,
  getPhysicalInventoryItems,
  scanBarcode,
  recordCount,
  deleteCountedItem,
  completePhysicalInventory
} from '../../services/physicalInventoryService';
import AuthContext from '../../context/AuthContext';

const PhysicalInventory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [scannedItems, setScannedItems] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [foundPart, setFoundPart] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completingInventory, setCompletingInventory] = useState(false);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [queueSize, setQueueSize] = useState(0);

  const barcodeInputRef = useRef(null);
  const inactivityTimerRef = useRef(null);

  // Load warehouses on initial render
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const data = await getWarehouses();
        setWarehouses(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching warehouses:', err);
        setError('Failed to load warehouses. Please try again.');
        setLoading(false);
      }
    };

    fetchWarehouses();

    // Clear inactivity timer on unmount
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);

  // Load inventory if ID is provided
  useEffect(() => {
    if (id) {
      const fetchInventory = async () => {
        try {
          setLoading(true);
          const data = await getPhysicalInventoryById(id);
          setInventory(data);
          setSelectedWarehouse(data.warehouse_id);

          // Load items for this inventory
          fetchInventoryItems(id);

          setLoading(false);
        } catch (err) {
          console.error('Error fetching inventory:', err);
          setError('Failed to load inventory. Please try again.');
          setLoading(false);
        }
      };

      fetchInventory();
    }
  }, [id]);

  // Load locations when warehouse is selected
  useEffect(() => {
    if (selectedWarehouse) {
      const fetchLocations = async () => {
        try {
          const data = await getLocations({ warehouse_id: selectedWarehouse });
          setLocations(data);
        } catch (err) {
          console.error('Error fetching locations:', err);
          setError('Failed to load locations. Please try again.');
        }
      };

      fetchLocations();
    }
  }, [selectedWarehouse]);

  // Focus barcode input when current location changes
  useEffect(() => {
    if (currentLocation && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [currentLocation]);

  const fetchInventoryItems = async (inventoryId) => {
    try {
      setRefreshing(true);
      const data = await getPhysicalInventoryItems(inventoryId, { page, limit: 50 });
      setScannedItems(data.items);
      setTotalItems(data.total);
      setRefreshing(false);
    } catch (err) {
      console.error('Error fetching inventory items:', err);
      setError('Failed to load inventory items. Please try again.');
      setRefreshing(false);
    }
  };

  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Reset timer for 5 minutes of inactivity
    inactivityTimerRef.current = setTimeout(() => {
      // Clear current location after inactivity
      setCurrentLocation(null);
      toast.info('Session timed out due to inactivity. Please scan a location to continue.');
    }, 5 * 60 * 1000);
  };

  const handleWarehouseChange = (e) => {
    setSelectedWarehouse(e.target.value);
    setCurrentLocation(null);
  };

  const handleStartInventory = async () => {
    if (!selectedWarehouse) {
      toast.error('Please select a warehouse');
      return;
    }

    try {
      setLoading(true);
      const data = await createPhysicalInventory({
        warehouse_id: selectedWarehouse,
        notes: 'Physical inventory count'
      });

      setInventory(data);
      setLoading(false);

      toast.success('Physical inventory started');

      // Redirect to the inventory page with ID
      navigate(`/inventory/physical/${data.id}`);
    } catch (err) {
      console.error('Error starting inventory:', err);
      setError('Failed to start inventory. Please try again.');
      setLoading(false);
    }
  };

  // Use a ref to store the barcode processing queue
  const barcodeQueueRef = useRef([]);
  const processingRef = useRef(false);

  // Function to process the next barcode in the queue
  const processNextBarcode = async () => {
    if (processingRef.current || barcodeQueueRef.current.length === 0) {
      return;
    }

    processingRef.current = true;
    setBarcodeLoading(true);

    try {
      const currentBarcode = barcodeQueueRef.current.shift();
      // Update queue size state to trigger re-render
      setQueueSize(barcodeQueueRef.current.length);

      const result = await scanBarcode(inventory.id, currentBarcode);

      if (result.type === 'location') {
        // If barcode is for a location
        setCurrentLocation(result.location);
        setFoundPart(null);
        toast.success(`Location: ${result.location.rack}-${result.location.shelf}`);
      } else if (result.type === 'part') {
        // If barcode is for a part
        if (!currentLocation) {
          toast.error('Please scan a location first');
          setBarcodeLoading(false);
          processingRef.current = false;

          // Process next barcode if any
          if (barcodeQueueRef.current.length > 0) {
            processNextBarcode();
          }
          return;
        }

        setFoundPart(result.part);
        toast.success(`Found part: ${result.part.internal_part_number}`);

        // Auto-record the count with quantity 1
        await handleRecordCount(result.part.id);
      }

      setBarcodeLoading(false);
      processingRef.current = false;

      // Process next barcode if any
      if (barcodeQueueRef.current.length > 0) {
        processNextBarcode();
      }
    } catch (err) {
      console.error('Error scanning barcode:', err);

      if (err.message === 'No item found with this barcode') {
        toast.error('Barcode not recognized');
      } else {
        setError('Error scanning barcode. Please try again.');
      }

      setBarcodeLoading(false);
      processingRef.current = false;

      // Process next barcode if any
      if (barcodeQueueRef.current.length > 0) {
        processNextBarcode();
      }
    }

    resetInactivityTimer();
  };

  const handleBarcodeScan = () => {
    if (!barcodeInput) return;

    // Add the current barcode to the queue
    barcodeQueueRef.current.push(barcodeInput);

    // Update queue size state to trigger re-render
    setQueueSize(barcodeQueueRef.current.length);

    // Clear the input field immediately to be ready for the next scan
    setBarcodeInput('');
    setError(null);

    // Start processing if not already processing
    if (!processingRef.current) {
      processNextBarcode();
    }
  };

  const handleRecordCount = async (partId) => {
    if (!currentLocation) {
      toast.error('Please scan a location first');
      return;
    }

    try {
      const countData = {
        location_id: currentLocation.id,
        part_id: partId || foundPart.id,
        quantity: quantity,
        notes: notes
      };

      const result = await recordCount(inventory.id, countData);

      // Show different message based on whether it's a new item or an existing one
      if (result.id) {
        const isNewItem = result.counted_quantity === quantity;
        toast.success(isNewItem
          ? `Count recorded: ${quantity}`
          : `Count updated: +${quantity} (Total: ${result.counted_quantity})`
        );
      } else {
        toast.success('Count recorded');
      }

      setQuantity(1);
      setNotes('');
      setFoundPart(null);

      // Refresh the items list
      fetchInventoryItems(inventory.id);
    } catch (err) {
      console.error('Error recording count:', err);
      setError('Failed to record count. Please try again.');
    }

    resetInactivityTimer();
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await deleteCountedItem(inventory.id, itemId);

      toast.success('Item removed from count');

      // Refresh the items list
      fetchInventoryItems(inventory.id);
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Failed to delete item. Please try again.');
    }
  };

  const handleCompleteInventory = async () => {
    setShowCompleteDialog(false);

    try {
      setCompletingInventory(true);
      const result = await completePhysicalInventory(inventory.id);

      toast.success('Physical inventory completed');
      setCompletingInventory(false);

      // Navigate to the discrepancy report
      navigate(`/inventory/physical/${inventory.id}/discrepancies`);
    } catch (err) {
      console.error('Error completing inventory:', err);
      setError('Failed to complete inventory. Please try again.');
      setCompletingInventory(false);
    }
  };

  const handleRefresh = () => {
    if (inventory) {
      fetchInventoryItems(inventory.id);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Physical Inventory
        </Typography>
        <Box>
          {inventory && (
            <>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{ mr: 1 }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/inventory/physical/list')}
                sx={{ mr: 1 }}
              >
                Back to Inventory Counts
              </Button>
              {inventory.status === 'in_progress' && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setShowCompleteDialog(true)}
                >
                  Complete Count
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!inventory ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Start New Physical Inventory
          </Typography>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="warehouse-select-label">Select Warehouse</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={selectedWarehouse}
                onChange={handleWarehouseChange}
                label="Select Warehouse"
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="primary"
              onClick={handleStartInventory}
              disabled={!selectedWarehouse}
              fullWidth
              sx={{ mt: 2 }}
            >
              Start Inventory Count
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Inventory Details
                </Typography>
                <Typography variant="body1">
                  <strong>Warehouse:</strong> {inventory.warehouse?.name}
                </Typography>
                <Typography variant="body1">
                  <strong>Status:</strong> {' '}
                  <Chip
                    label={inventory.status === 'in_progress' ? 'In Progress' : 'Completed'}
                    color={inventory.status === 'in_progress' ? 'primary' : 'success'}
                    size="small"
                  />
                </Typography>
                <Typography variant="body1">
                  <strong>Started:</strong> {new Date(inventory.started_at).toLocaleString()}
                </Typography>
                {inventory.completed_at && (
                  <Typography variant="body1">
                    <strong>Completed:</strong> {new Date(inventory.completed_at).toLocaleString()}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Current Location
                </Typography>
                {currentLocation ? (
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={`${currentLocation.warehouse.name} - ${currentLocation.rack}-${currentLocation.shelf}`}
                      color="primary"
                      sx={{ fontSize: '1.1rem', py: 2, px: 1 }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No location selected. Scan a location barcode to begin.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {inventory.status === 'in_progress' && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Scan Items
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Scan Barcode
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <TextField
                      fullWidth
                      label="Scan or Enter Barcode"
                      value={barcodeInput}
                      onChange={(e) => setBarcodeInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && barcodeInput.trim()) {
                          handleBarcodeScan();
                        }
                      }}
                      inputRef={barcodeInputRef}
                      autoFocus
                      placeholder="Scan or enter barcode"
                      InputProps={{
                        endAdornment: queueSize > 0 && (
                          <Typography variant="caption" color="primary" sx={{ mr: 2 }}>
                            Queue: {queueSize}
                          </Typography>
                        )
                      }}
                    />
                    <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
                      {barcodeLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton
                          onClick={handleBarcodeScan}
                          disabled={!barcodeInput.trim()}
                          edge="end"
                        >
                          <SearchIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {foundPart && (
                  <Card sx={{ mt: 2, border: '1px solid', borderColor: 'primary.main' }}>
                    <CardContent>
                      <Typography variant="h6">
                        {foundPart.internal_part_number}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {foundPart.description}
                      </Typography>
                      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                          label="Quantity"
                          type="number"
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          InputProps={{ inputProps: { min: 1 } }}
                          size="small"
                          sx={{ width: 100 }}
                        />
                        <TextField
                          label="Notes (Optional)"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          size="small"
                          sx={{ flexGrow: 1 }}
                        />
                      </Box>
                    </CardContent>
                    <CardActions>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleRecordCount()}
                        fullWidth
                      >
                        Record Count
                      </Button>
                    </CardActions>
                  </Card>
                )}
              </Box>
            </Paper>
          )}

          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Scanned Items {refreshing && <CircularProgress size={20} sx={{ ml: 1 }} />}
                </Typography>
                {queueSize > 0 && (
                  <Chip
                    label={`Processing Queue: ${queueSize}`}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
            </Box>

            {scannedItems.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No items scanned yet. Scan a location and then scan parts to begin counting.
                </Typography>
              </Box>
            ) : (
              <List>
                {scannedItems.map((item) => (
                  <ListItem
                    key={item.id}
                    divider
                    secondaryAction={
                      inventory.status === 'in_progress' && (
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" component="span" fontWeight="medium">
                            {item.part.internal_part_number}
                          </Typography>
                          <Typography variant="body2" component="span" color="text.secondary">
                            {item.part.description}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={`${item.location.rack}-${item.location.shelf}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Typography variant="body2" component="span">
                            Qty: <strong>{item.counted_quantity}</strong>
                          </Typography>
                          <Typography variant="body2" component="span" color="text.secondary">
                            {new Date(item.scan_timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </>
      )}

      {/* Complete Inventory Dialog */}
      <Dialog
        open={showCompleteDialog}
        onClose={() => setShowCompleteDialog(false)}
      >
        <DialogTitle>Complete Physical Inventory</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to complete this physical inventory count?
            This will generate a discrepancy report comparing the counted quantities
            with the system inventory.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCompleteInventory}
            color="primary"
            variant="contained"
            disabled={completingInventory}
          >
            {completingInventory ? <CircularProgress size={24} /> : 'Complete Inventory'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PhysicalInventory;
